import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { APP_NAME } from '@hazel/shared'

import { useBarTitleShown } from './bar-title.js'

/**
 * The bar across the top, the same one on every screen.
 *
 * Extracted so the host display can have it too. The room used to be framed
 * differently from the games it was showing — no bar above, no legend below —
 * on the reasoning that "nobody walks up to a television to click things".
 * That was true when the room had nothing to press; it now has a card menu,
 * shortcut keys throughout and a legend, so the premise had been overtaken
 * and the two screens simply looked unrelated.
 *
 * The right-hand side is handed in rather than read from the session here.
 * The host has no session and never will — the television is a screen, not a
 * player — so a header that reached for one would have to be told not to on
 * the one screen that most needed the bar.
 */
export function AppHeader({ children }: { children?: ReactNode }) {
  /*
    The game on screen, if there is one, in place of the app's name.

    Read here rather than passed in, so neither layout has to thread it
    through — the host display would have had to hand down something it knows
    nothing about, since the room's game lives inside `Host`.
  */
  const playing = useBarTitleShown()

  return (
    <header className="border-border bg-surface-raised border-b">
      {/* Full width on the television: the name hard left, the player hard
          right. It used to sit in the same centred column the games did
          before they were widened, which on a 16:9 screen is a huddle in the
          middle with two empty thirds either side. */}
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 p-4 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:px-8">
        {/*
          A heading while she is playing, a link the rest of the time.

          Not a link during a round, deliberately: it sits where a thumb lands
          at the top of the screen, and one tap would drop the round. The ways
          out are the ones the app already teaches — "Back to the games" on a
          phone or a laptop, and Escape on the television, where that link is
          hidden and the pinned legend says so.

          And only a heading when a game is on. A bar that always emitted one
          would put a second `h1` on every page that has its own — `/profile`
          is headed with her name.
        */}
        {playing ? (
          <h1 className="font-display text-(length:--text-heading) m-0 truncate leading-tight">
            {playing}
          </h1>
        ) : (
          <Link to="/" className="font-display text-(length:--text-heading) no-underline">
            {APP_NAME}
          </Link>
        )}

        {children ? <nav className="flex items-center gap-3">{children}</nav> : null}
      </div>
    </header>
  )
}
